import { useGetEnrollmentJointDisclosuresData, useQueryClient, useSubmitEnrollmentJointDisclosuresData } from '@egw/api'
import { Button, Alert, AlertType, GenericError, Tag } from '@egw/components'
import { configClient } from '@egw/config'
import { WEALTH_ROUTES } from '@egw/constants'
import { sendEvent } from '@egw/events'
import { useTranslation } from '@egw/i18n'
import { EnrollmentJointLayout } from '@egw/layouts'
import { Logger } from '@egw/logger'
import { UserIcon } from '@heroicons/react/16/solid'
import { useEffect, useMemo, type FC } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useNavigate, useLocation } from 'react-router'
import { DisclosureSection, LoadingAnimation, PageHeader } from '../../../components'
import { EnrollmentPageLoading } from '../../../components/page-loading'
import { type Config } from '../../../config/config'
import { EDataLayerEventWealth } from '../../../constants/events/EDataLayerEventWealth'
import { ROUTES } from '../../../constants/routes/routes-generic'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'
import { getArticlePath } from '../../../utils/disclosures/get-disclosures-path'
import { getJointDisclosures } from '../../../utils/disclosures/get-joint-disclosures'
import { newPathIsDifferent } from '../../../utils/general/new-path-is-different'
import { useHandleEnrollmentErrors } from '../../../utils/joint/api-errors/enrollment-error-handler'

const log = new Logger('Enrollment - Joint - Disclosures')

export const DisclosuresPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()
  const location = useLocation()
  const config = configClient.getConfig() as Config

  const queryClient = useQueryClient()
  const { data, isLoading, error } = useGetEnrollmentJointDisclosuresData()
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentJointDisclosuresData()
  const applicationId = data && !data.isError ? data.activePerson?.applicationId : ''

  // Log initial data loading errors
  useEffect(() => {
    if (error) {
      log.error('Error loading enrollment joint disclosures data', error)
    }
  }, [error])

  // Log form submission errors
  useEffect(() => {
    if (submitError) {
      log.error('Error submitting enrollment joint disclosures form', submitError)
    }
  }, [submitError])

  useEffect(() => {
    sendEvent(EDataLayerEventWealth.USER_DISCLOSURES_STARTED, { applicationId })
  }, [applicationId])

  useEffect(() => {
    // Clear the query client here, to have fresh / correct data for Enrollee 1 vs Enrollee 2
    queryClient.clear()
  }, [queryClient])

  const disclosures = useMemo(() => {
    return getJointDisclosures(config.zendeskArticles, data?.activePerson?.unacceptedInvestmentDisclosures ?? [])
  }, [config.zendeskArticles, data?.activePerson?.unacceptedInvestmentDisclosures])

  const {
    control,
    handleSubmit,
    formState: { errors: _formErrors, isValid },
    watch,
  } = useForm<{
    egwa: boolean
    apex?: boolean
  }>({
    defaultValues: {
      egwa: false,
    },
  })

  const isCheckedEGWA = watch('egwa')

  // Dispatch events when disclosure section is checked
  useEffect(() => {
    if (isCheckedEGWA) {
      sendEvent(EDataLayerEventWealth.BANKHYCHYS_WEALTHADVISORS_CONSENT, { applicationId })
    }
  }, [isCheckedEGWA, applicationId])

  // Protect route
  useValidateRoute(data?.activePerson?.path)

  // Error handler for enrollment errors
  const handleEnrollmentErrors = useHandleEnrollmentErrors('Joint - Disclosures')

  const onSubmit = handleSubmit(async (): Promise<void> => {
    if (isValid) {
      const apexDisclosuresPayload =
        data?.activePerson?.unacceptedInvestmentDisclosures?.map((e) => e.disclosureId) ?? []

      const payload = {
        activePerson: {
          taxCertificationForUSPersonsDisclosure: {
            id: getArticlePath(config.zendeskArticles.taxCertification),
            accepted: true,
          },
          investmentAdvisoryAgreementDisclosure: {
            id: getArticlePath(config.zendeskArticles.investmentAdvisoryAgreement),
            accepted: true,
          },
          advWrapFeeDisclosure: {
            id: getArticlePath(config.zendeskArticles.advPart2A2b),
            accepted: true,
          },
          advisoryPrivacyNotice: {
            id: getArticlePath(config.zendeskArticles.privacyPolicyAdvisors),
            accepted: true,
          },
          customerRelationshipSummaryDisclosure: {
            id: getArticlePath(config.zendeskArticles.customerRelationshipAgreement),
            accepted: true,
          },
          acceptedInvestmentDisclosureIds: apexDisclosuresPayload,
        },
      }

      sendEvent(EDataLayerEventWealth.BANKHYCHYS_CREATEACCOUNT_SUBMIT, { applicationId })
      sendEvent(EDataLayerEventWealth.MILESTONE_DISCLOSURES_CONSENT, {
        applicationId,
        disclosures: payload,
      })
      sendEvent(EDataLayerEventWealth.MILESTONE_CREATE_ACCOUNT_CLICKED, { applicationId })

      try {
        const response = await mutate(payload)

        // Check for API errors in the response
        if (response && handleEnrollmentErrors(response)) {
          return
        }

        // If path exists and is different
        if (response?.activePerson?.path && newPathIsDifferent(location.pathname, response.activePerson.path)) {
          sendEvent(EDataLayerEventWealth.EGM_SUBMISSION_DISCLOSURES_ACCEPTED, { applicationId })
          sendEvent(EDataLayerEventWealth.MILESTONE_ACCOUNT_CREATED, { applicationId })
          sendEvent(EDataLayerEventWealth.BANKHYCHYS_ACCOUNT_CREATED, { applicationId })
          void navigate(response.activePerson.path)
        } else if (!response?.activePerson?.path) {
          // If no path from BE, redirect to error
          log.error('No path returned from backend, redirecting to error page')
          void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ROUTES.ERROR.path}`)
        }
      } catch (e) {
        log.error('Error submitting disclosures form', e)
      }
    }
  })

  const profileName = data?.activePerson?.name?.firstName ? `${data.activePerson.name.firstName}'s Profile` : ''

  return (
    <EnrollmentJointLayout title={t('joint.disclosures.page_title')}>
      {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
      {isLoading ? <EnrollmentPageLoading testId="disclosures-loading" /> : null}

      {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
      {!isLoading && !error && isSubmitting ? (
        <div className="flex flex-col justify-center items-center flex-1 w-full">
          <LoadingAnimation />
          <p className="typo-primary-xl-light mt-md">{t(`joint.disclosures.loading`)}</p>
        </div>
      ) : null}

      {!isLoading && !error && !isSubmitting ? (
        <form onSubmit={(e) => void onSubmit(e)} className="w-full flex-1 flex flex-col justify-start">
          <Tag icon={<UserIcon className="text-olive-400" />} className="mb-xs">
            {profileName}
          </Tag>
          {/* No backPath here */}
          <PageHeader title={t('joint.disclosures.title')} />
          <p className="typo-primary-base text-text-meta mb-md">
            {t('joint.disclosures.description_1_yeet_coastal')}
          </p>

          <p className="typo-primary-base text-text-meta mb-md">{t('joint.disclosures.description_2')}</p>

          {disclosures.map((e, i) => {
            return (
              <Controller
                name={e.name as 'egwa' | 'apex'}
                control={control}
                rules={{
                  required: t('common.required_field'),
                }}
                render={({ field }) => (
                  <DisclosureSection
                    checkboxHelperText={e.helper}
                    checkboxLabel={e.title}
                    expandedLinks={e.links}
                    expandedText={e.expanded}
                    hasBorder={disclosures.length !== i + 1}
                    testId={`disclosure-${e.title.toLowerCase().replaceAll(' ', '-')}`}
                    onCheckboxChange={field.onChange}
                  />
                )}
                key={`disclosure-${e.title.replaceAll(' ', '-')}`}
              />
            )
          })}

          <div className="flex-1 md:hidden" />

          {submitError ? (
            <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" />
          ) : null}

          {/* AUTO GENERATED COMPONENTS */}

          <Button
            type="submit"
            className="w-full md:w-min mt-xl"
            isDisabled={!isValid}
            isLoading={isSubmitting}
            testId="primaryAction_button"
          >
            {t('joint.disclosures.continue')}
          </Button>
        </form>
      ) : null}

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {(data?.isError ?? (data?.errors && data.errors.length > 0)) ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('joint.disclosures.invalid_magic_link')} />
        </div>
      ) : null}

      {!(data?.isError ?? (data?.errors && data.errors.length > 0)) && error ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}
    </EnrollmentJointLayout>
  )
}
